import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import axios from "axios";
import CONFIG from "@/assets/js/config.custom.js";
import Select2Multiple from 'v-select2-multiple-component';
import store from "@/store";

export default {
  name: "EarlyYearList",
  components: {
    MainLayout,
    Loading,
    Select2Multiple
  },

  data() {
    return {
      isLoading: false,
      categories: [],
      terms: {},
      academicYearId: null,
      student_name: '',
      noDataMsg: CONFIG.NO_DATA_EXIST,
      classId: null,
      inputData: {
        display_status: {},
        item_status: {}
      },
      lockedTermIds: [],
    }
  },

  computed: {
    isPrincipalRole() {
      return Number(store.state.user?.role) === CONFIG.ROLE.ID.PRINCIPAL;
    },

    displayOptions() {
      return Object.entries(CONFIG.REPORT_CARD_DISPLAY_STATUS).map(([id, text]) => ({
        id: parseInt(id),
        text: text
      }));
    },

    statusOptions() {
      return Object.keys(CONFIG.REPORT_CARD_STATUS).map(key => ({
        id: key,
        text: key
      }));
    },

    studentExists() {
      return this.student_name && 
             this.student_name.trim() !== '' && 
             this.academicYearId && 
             this.terms && 
             Object.keys(this.terms).length > 0;
    },

    groupedCategories() {
      if (!Array.isArray(this.categories) || this.categories.length === 0) {
        return [];
      }

      return this.categories.reduce((groups, category) => {
        const title = category.category_title || '';
        const currentGroup = groups[groups.length - 1];

        if (!currentGroup || currentGroup.title !== title) {
          groups.push({
            title,
            categories: []
          });
        }

        groups[groups.length - 1].categories.push(category);
        return groups;
      }, []);
    }
  },

  methods: {
    isTermLocked(termId) {
      return !this.isPrincipalRole && this.lockedTermIds.includes(Number(termId));
    },

    loadPageData() {
      this.isLoading = true;

      Promise.all([
        axios.get(`early-year/terms/${this.academicYearId}`),
        axios.get('early-year/categories', {
          params: {
            student_id: this.studentId,
            academic_year_id: this.academicYearId
          }
        })
      ])
        .then(([termsRes, categoriesRes]) => {
          this.terms = termsRes.data;
          this.categories = categoriesRes.data;

          const displayStatus = {};
          this.terms.forEach(term => {
            displayStatus[term.id] = '';
          });
          this.inputData.display_status = displayStatus;

          this.initializeItemStatus();
          return this.loadExistingData();
        })
        .catch(error => {
          if (error.response && error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
            return;
          }
          console.error('Error loading early year edit page:', error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    initializeItemStatus() {
      const itemStatus = {};

      this.terms.forEach(term => {
        this.categories.forEach(category => {
          category.items.forEach(item => {
            itemStatus[`${term.id}_${item.id}`] = '';
          });
        });
      });

      this.inputData.item_status = itemStatus;
    },

    saveReportCard() {
      this.isLoading = true;

      const displayStatus = {};
      this.terms.forEach(term => {
        if (this.isTermLocked(term.id)) {
          return;
        }

        const value = this.inputData.display_status[term.id];
        if (value && value !== '') {
          displayStatus[term.id] = value;
        }
      });

      const gradingDetails = [];
      this.terms.forEach(term => {
        if (this.isTermLocked(term.id)) {
          return;
        }

        this.categories.forEach(category => {
          category.items.forEach(item => {
            const key = `${term.id}_${item.id}`;
            const grading = this.inputData.item_status[key];
            
            if (grading && grading.trim() !== '') {
              gradingDetails.push({
                term_id: term.id,
                reportcard_item_id: item.id,
                grading: grading
              });
            }
          });
        });
      });

      const payload = {
        login_id: store.state.user.id,
        student_id: this.studentId,
        class_id: this.classId,
        academic_year_id: this.academicYearId,
        display_status: displayStatus,
        grading_details: gradingDetails
      };

      axios.post('early-year/save-report-card', payload)
        .then(res => {
          this.isLoading = false;
          if (res.status == 200) {
            this.$notification.success("Report Card Saved Successfully", {
              timer: 3,
              position: "bottomCenter"
            });
            this.$router.push({
              name: 'earlyYearList'
            })
          } else if (res.status == 201) {
            this.$notification.error(res.data.message, {
              timer: 3,
              position: "bottomCenter"
            });
          }
        })
        .catch(error => {
          this.isLoading = false;
          const message = error.response.data.message;
          if (error.response.status == 422) {
            this.$notification.error("Something Wrong And Check Your Data.", {
              timer: 3,
              position: "bottomCenter"
            });
          }
          else if (error.response.status == 400) {
            this.$notification.error(message, {
              timer: 5,
              position: "bottomCenter"
            });
          }
          else if (error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
        })
    },

    loadExistingData() {
      return axios.get(`early-year/get-report-card/${this.studentId}/${this.academicYearId}`)
        .then(res => {
          if (!res.data) {
            return;
          }

          this.student_name = res.data.student.student_name;
          this.classId = res.data.student.class_id;
          this.lockedTermIds = [];

          const displayStatus = { ...this.inputData.display_status };
          const itemStatus = { ...this.inputData.item_status };

          res.data.report_card.forEach(grading => {
            displayStatus[grading.term_id] = grading.status;
            if (Number(grading.status) === CONFIG.REPORT_CARD_DISPLAY_STATUS_KEYS.APPROVE) {
              this.lockedTermIds.push(Number(grading.term_id));
            }

            grading.details.forEach(detail => {
              itemStatus[`${grading.term_id}_${detail.reportcard_item_id}`] = detail.grading;
            });
          });

          this.inputData.display_status = displayStatus;
          this.inputData.item_status = itemStatus;
        })
        .catch(error => {
          console.error('Error loading existing data:', error);
        });
    }
  },

  mounted() {
    this.academicYearId = this.$route.params.academicYearId;
    this.studentId = this.$route.params.id;
    this.loadPageData();
  }
};
